import { useEffect, useState } from 'react';
import { LayoutGrid, Globe, Settings, Menu, X, LogOut, User } from 'lucide-react';
import * as Dialog from '@radix-ui/react-dialog';
import { cn } from '../lib/cn';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/DropdownMenu';
import { useSession } from '../contexts/SessionContext';
import type { Tab } from '../routes';

interface Props {
  tab: Tab;
  onTabChange: (t: Tab) => void;
  onHome: () => void;
}

const tabs: { id: Tab; label: string; icon: typeof LayoutGrid }[] = [
  { id: 'sites', label: 'Sites', icon: LayoutGrid },
  { id: 'domains', label: 'Domains', icon: Globe },
  { id: 'settings', label: 'Settings', icon: Settings },
];

export function NavBar({ tab, onTabChange, onHome }: Props) {
  const { user, logout } = useSession();
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    if (mobileOpen) setMobileOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  return (
    <header className="sticky top-0 z-30 border-b border-zinc-200/80 bg-white/85 backdrop-blur-md supports-[backdrop-filter]:bg-white/70">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <div className="flex h-14 items-center justify-between">
          {/* Brand */}
          <div className="flex items-center gap-2">
            <Dialog.Root open={mobileOpen} onOpenChange={setMobileOpen}>
              <Dialog.Trigger
                aria-label="Open menu"
                className="md:hidden -ml-2 inline-flex h-9 w-9 items-center justify-center rounded-md text-zinc-600 hover:bg-zinc-100"
              >
                <Menu className="h-5 w-5" />
              </Dialog.Trigger>
              <Dialog.Portal>
                <Dialog.Overlay className="fixed inset-0 z-40 bg-zinc-900/40 backdrop-blur-[2px] data-[state=open]:animate-dialog-overlay-in" />
                <Dialog.Content
                  aria-describedby={undefined}
                  className="fixed inset-y-0 left-0 z-50 w-72 bg-white shadow-soft border-r border-zinc-200 p-5 flex flex-col data-[state=open]:animate-drawer-in"
                >
                  <div className="flex items-center justify-between mb-7">
                    <Dialog.Title className="flex items-center gap-2">
                      <Logo />
                      <span className="font-semibold tracking-tight2 text-zinc-900">Sitekit</span>
                    </Dialog.Title>
                    <Dialog.Close
                      aria-label="Close menu"
                      className="inline-flex h-9 w-9 items-center justify-center rounded-md text-zinc-500 hover:bg-zinc-100"
                    >
                      <X className="h-4 w-4" />
                    </Dialog.Close>
                  </div>

                  <nav className="flex flex-col gap-1">
                    {tabs.map((t) => {
                      const Icon = t.icon;
                      const active = tab === t.id;
                      return (
                        <button
                          key={t.id}
                          onClick={() => {
                            onTabChange(t.id);
                            setMobileOpen(false);
                          }}
                          className={cn(
                            'flex items-center gap-3 rounded-lg px-3 h-11 text-sm font-medium transition-colors text-left',
                            active
                              ? 'bg-zinc-900 text-white'
                              : 'text-zinc-700 hover:bg-zinc-100',
                          )}
                        >
                          <Icon className="h-4 w-4" />
                          {t.label}
                        </button>
                      );
                    })}
                  </nav>

                  <div className="mt-auto pt-4 border-t border-zinc-100">
                    <div className="px-3 py-2 text-[12px] text-zinc-500">
                      Signed in as
                    </div>
                    <div className="px-3 text-sm text-zinc-900 truncate">
                      {user?.email}
                    </div>
                    <button
                      onClick={() => {
                        void logout();
                      }}
                      className="mt-3 w-full flex items-center justify-center gap-2 h-10 rounded-lg border border-zinc-200 text-sm font-medium text-zinc-700 hover:bg-zinc-50"
                    >
                      <LogOut className="h-3.5 w-3.5" />
                      Sign out
                    </button>
                  </div>
                </Dialog.Content>
              </Dialog.Portal>
            </Dialog.Root>

            <button
              type="button"
              onClick={onHome}
              className="flex items-center gap-2 -ml-1 px-1 py-1 rounded-md hover:bg-zinc-100/70 transition-colors"
            >
              <Logo />
              <span className="text-[15px] font-semibold tracking-tight2 text-zinc-900">
                Sitekit
              </span>
            </button>
          </div>

          {/* Center tabs (desktop) */}
          <nav className="hidden md:flex items-center gap-1 absolute left-1/2 -translate-x-1/2">
            {tabs.map((t) => {
              const Icon = t.icon;
              const active = tab === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => onTabChange(t.id)}
                  className={cn(
                    'relative inline-flex items-center gap-1.5 h-9 px-3 rounded-md text-[13.5px] font-medium transition-colors',
                    active
                      ? 'text-zinc-900'
                      : 'text-zinc-500 hover:text-zinc-800',
                  )}
                >
                  <Icon className="h-3.5 w-3.5 opacity-80" />
                  {t.label}
                  {active && (
                    <span className="absolute left-2 right-2 -bottom-[15px] h-px bg-zinc-900" />
                  )}
                </button>
              );
            })}
          </nav>

          {/* User */}
          <DropdownMenu>
            <DropdownMenuTrigger
              aria-label="User menu"
              className="inline-flex items-center gap-2 h-9 rounded-full pl-1 pr-2.5 hover:bg-zinc-100 transition-colors"
            >
              <div className="h-7 w-7 rounded-full bg-zinc-900 text-white text-[11px] font-semibold flex items-center justify-center">
                {(user?.displayName ?? user?.email ?? '?')
                  .replace(/[^a-zA-Z0-9]/g, '')
                  .slice(0, 2)
                  .toUpperCase() || 'U'}
              </div>
              <span className="hidden sm:block text-[13px] font-medium text-zinc-700 max-w-[140px] truncate">
                {user?.displayName ?? user?.email?.split('@')[0]}
              </span>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[220px]">
              <DropdownMenuLabel>Account</DropdownMenuLabel>
              <div className="px-2.5 pb-2 text-[12.5px] text-zinc-500 truncate">{user?.email}</div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => onTabChange('settings')}>
                <User className="h-3.5 w-3.5" />
                Account settings
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem danger onClick={() => void logout()}>
                <LogOut className="h-3.5 w-3.5" />
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}

function Logo() {
  return (
    <div className="relative h-7 w-7 rounded-lg bg-gradient-to-br from-zinc-900 to-zinc-700 flex items-center justify-center shadow-card">
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="white"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M3 9l9-6 9 6-9 6-9-6z" />
        <path d="M3 15l9 6 9-6" />
      </svg>
    </div>
  );
}
